'use client';
import { isButton } from '@bw/apis/contentful';
import { Image } from '@bw/elements';
import { useAppContext } from '@bw/ui';
import { getCookie } from 'cookies-next';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { BlockType } from '../../factories/types';
import { annotate } from '../../utils/annotate';
import { defined } from '../../utils/defined';
import mobileStyles from './header-menu/header-mobile-menu.module.scss';
import { DesktopMenu } from './header-menu/HeaderDesktopMenu';
import { MobileMenu } from './header-menu/HeaderMobileMenu';
import styles from './Header.module.scss';
import { HeaderCtaSection } from './HeaderCtaSection';
import { HeaderNavItem, isMenu } from './types';

interface ClientHeaderProps {
  header: BlockType<'header'>;
}

export const ClientHeader = ({ header }: ClientHeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerWrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [activeHeaderItem, setActiveHeaderItem] = useState<HeaderNavItem | null>(null);
  const [isMobile, setIsMobile] = useState(true); // TODO: use media query instead
  const [heightChange, setHeightChange] = useState(0);
  const { showFormStepper } = useAppContext();
  const { announcementBannerHeight } = useAppContext();

  const logo = header.fields.logo;

  useEffect(() => {
    setIsMobile(window.innerWidth < 1150);

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1150);
    };
    const handleHeightChange = () => {
      setHeightChange(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleHeightChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleHeightChange);
    };
  }, []);

  useEffect(() => {
    const announcementClosedCookie = getCookie('announcementClosed');
    if (!announcementClosedCookie && headerWrapperRef.current && !isMobile) {
      // const headerWrapperHeight = headerWrapperRef.current.clientHeight;

      // setMenuHeight(
      //   window.innerHeight -
      //   ((headerWrapperHeight || 80) + announcementBannerHeight)
      // );
    } else if (headerWrapperRef.current) {
      // const headerWrapperHeight = headerWrapperRef.current.clientHeight;
      // setMenuHeight(window.innerHeight - (headerWrapperHeight || 80));
    }
  }, [menuOpen, heightChange, isMobile, announcementBannerHeight]);

  const handleFocusWithinModal = (e) => {
    const modal = headerRef.current;
    if (!modal) {
      return;
    }
    const focusableElements = modal.querySelectorAll(
      'a, button, [tabindex]:not([tabindex="-1"])'
    );
    const focusableElementsArray =
      Array.prototype.slice.call(focusableElements);
    const firstFocusableElement = focusableElementsArray[0];
    const lastFocusableElement =
      focusableElementsArray[focusableElementsArray.length - 1];
    if (e.key === 'Tab' || e.keyCode === 9) {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          e.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          e.preventDefault();
          firstFocusableElement.focus();
        }
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !showFormStepper) {
        setMenuOpen(false);
        setActiveHeaderItem(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, showFormStepper]);

  useEffect(() => {
    const hdrRef = headerRef?.current;
    const handleKeyDown = (event) => {
      handleFocusWithinModal(event);
    };

    if (menuOpen && headerRef?.current) {
      hdrRef?.addEventListener('keydown', handleKeyDown);

      return () => {
        hdrRef?.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    setActiveHeaderItem(null);
  };

  const handleMenuItemClick = (menuItem: HeaderNavItem) => {
    if (!menuOpen) {
      setMenuOpen(true);
      setActiveHeaderItem(menuItem);
    } else if (menuOpen && activeHeaderItem === menuItem) {
      setMenuOpen(false);
      setActiveHeaderItem(null);
    } else {
      setActiveHeaderItem(menuItem);
    }
  };
  const handleMenuClose = () => {
    if (menuOpen) {
      setMenuOpen(false);
      setActiveHeaderItem(null);
    }
  };

  const barClassName = `${styles['bar']} ${menuOpen ? styles['bar--clicked'] : ''}`;

  const onBackClick = () => {
    setActiveHeaderItem(null);
  };

  const onHeaderItemClick = (headerItem: HeaderNavItem) => {
    setActiveHeaderItem(headerItem);
  }

  return (
    <>
      {/* <AnnouncementBar
				customClass={menuOpen && isMobile ? 'announcementBar--hide' : ''}
			/> */}

      <header className={styles.header + ' emphasized'} ref={headerRef}>
        <div className={styles.headerWrapper} ref={headerWrapperRef}>
          <Link
            href='/'
            title={logo?.fields.title}
            className={styles.headerLogo}
            onClick={handleMenuClose}
            {...annotate(logo, 'file')}
          >
            <Image
              src={logo?.fields.file?.url || ''}
              alt='Back to home'
              width={170}
              height={24}
              priority={true}
            />
          </Link>
          <nav aria-label="Header menu" className={styles.logoMenuWrapper}>
            <ul className={styles.navList}>
              {defined(header.fields.navigation).map((menuItem, index) =>
                <li
                  key={index}
                  {...annotate(menuItem)}
                  className={`${styles.navItem} ${activeHeaderItem === menuItem ? styles['navItem--activeMenu'] : ''}`}
                >
                  {isMenu(menuItem) ? (
                    <div
                      tabIndex={0}
                      className={styles.navContent}
                      onClick={() => {
                        handleMenuItemClick(menuItem);
                      }}
                      onKeyDown={(e) => {
                        // Handle Enter key press
                        if (e.key === 'Enter') {
                          handleMenuItemClick(menuItem);
                        }
                      }}
                      aria-haspopup="true"
                      role="button"
                    >
                      <span>{menuItem.fields.title}</span>
                      <ChevronDown />
                    </div>
                  ) : (
                    <span aria-haspopup="false">
                      <Link
                        href={isButton(menuItem) && menuItem.fields.url || ''}
                        onClick={handleMenuClose}
                      >
                        {menuItem.fields.title}
                      </Link>
                    </span>
                  )}
                </li>
              )}
            </ul>
          </nav>

          <button
            title="toggle menu"
            className={styles.hamburgerButton}
            type="button"
            onClick={toggleMenu}
          >
            {menuOpen ? (
              <span className={styles.hamburgerText}> Close</span>
            ) : (
              null
            )}
            <span className={styles.hamburgerIcon}>
              <span className={barClassName}></span>
              <span className={barClassName}></span>
              <span className={barClassName}></span>
            </span>
          </button>

          <HeaderCtaSection actions={header.fields.actions} className={styles.desktopCta} />
        </div>

        <div className={`${styles.megaMenu} ${menuOpen ? styles['megaMenu--opened'] : ''}`}>
          {isMobile ? (
            <MobileMenu
              activeHeaderItem={activeHeaderItem}
              onItemSelected={onHeaderItemClick}
              onBackClick={onBackClick}
              onCloseClick={handleMenuClose}
              menuContent={defined(header.fields.navigation)}
              ctaSection={
                <HeaderCtaSection
                  actions={header.fields.actions}
                  className={mobileStyles.mobileButtonWrapper}
                />
              }
            />
          ) : (
            activeHeaderItem &&
            <DesktopMenu
              onMenuItemClick={handleMenuClose}
              activeMenuItem={activeHeaderItem}
              onClose={handleMenuClose}
            />
          )}
        </div>
      </header>
    </>
  );
}

const ChevronDown = () =>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
  >
    <path
      height="20"
      d="M4.75977 6L8.75977 10L12.7598 6"
      stroke="white"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
